/**
 * Guest Issues / Complaint Resolution Tab
 * Operational interface for front desk staff to log and resolve guest complaints
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  AlertTriangle,
  CheckCircle,
  Shield,
  DollarSign,
  Plus,
  Clock,
  User,
  FileText,
  Bell
} from 'lucide-react';
import { BACKEND_URL } from '@/lib/backendUrl';


export function GuestIssuesTab({ hotelId, currentUser }) {
  const [loading, setLoading] = useState(true);
  const [allowances, setAllowances] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [showNewComplaint, setShowNewComplaint] = useState(false);
  
  // New complaint form
  const [newComplaint, setNewComplaint] = useState({
    guest_name: '',
    room_number: '',
    complaint_type: '',
    description: '',
    resolution_action: 'discount',
    amount_applied: 0
  });

  useEffect(() => {
    loadData();
  }, [hotelId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load complaint allowances (policies)
      const allowancesRes = await fetch(`${BACKEND_URL}/api/hotels/${hotelId}/complaint-allowances`);
      const allowancesData = await allowancesRes.json();
      
      if (allowancesData.success) {
        setAllowances(allowancesData.allowances || []);
      }

      // Load complaint history
      const complaintsRes = await fetch(`${BACKEND_URL}/api/hotels/${hotelId}/complaints`);
      const complaintsData = await complaintsRes.json();
      
      if (complaintsData.success) {
        setComplaints(complaintsData.complaints || []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load complaint data');
    } finally {
      setLoading(false);
    }
  };

  const handleComplaintTypeChange = (type) => {
    const allowance = allowances.find(a => a.complaint_type === type);
    setNewComplaint(prev => ({
      ...prev,
      complaint_type: type,
      resolution_action: allowance?.action_type || 'discount',
      amount_applied: 0
    }));
  };

  const handleSubmitComplaint = async () => {
    // Validation
    if (!newComplaint.guest_name || !newComplaint.complaint_type || !newComplaint.amount_applied) {
      toast.error('Please fill in all required fields');
      return;
    }

    const allowance = allowances.find(a => a.complaint_type === newComplaint.complaint_type);
    
    if (!allowance) {
      toast.error('Invalid complaint type');
      return;
    }

    if (newComplaint.amount_applied > allowance.max_amount) {
      toast.error(`Amount exceeds maximum allowed ($${allowance.max_amount}). Manager approval required.`);
      return;
    }

    if (!allowance.frontdesk_can_approve && currentUser.role === 'staff') {
      toast.error('This complaint type requires manager approval');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/hotels/${hotelId}/complaints`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newComplaint,
          resolved_by: currentUser.id,
          resolved_by_name: currentUser.name,
          resolved_at: new Date().toISOString(),
          status: 'resolved'
        })
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Complaint resolved successfully! Guest will be notified.');
        setShowNewComplaint(false);
        setNewComplaint({
          guest_name: '',
          room_number: '',
          complaint_type: '',
          description: '',
          resolution_action: 'discount',
          amount_applied: 0
        });
        loadData();
      } else {
        toast.error(data.error || 'Failed to submit complaint');
      }
    } catch (error) {
      console.error('Error submitting complaint:', error);
      toast.error('Failed to submit complaint');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  const getActionColor = (action) => {
    switch (action) {
      case 'discount': return 'bg-blue-100 text-blue-700';
      case 'refund': return 'bg-green-100 text-green-700';
      case 'upgrade': return 'bg-purple-100 text-purple-700';
      case 'complimentary': return 'bg-orange-100 text-orange-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="space-y-6" data-testid="guest-issues-content">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold heading-font mb-2">Guest Issues & Complaints</h2>
          <p className="text-muted-foreground">Resolve guest complaints quickly with pre-approved allowances</p>
        </div>
        <Button
          onClick={() => setShowNewComplaint(!showNewComplaint)}
          data-testid="new-complaint-button"
          className="bg-rose-600 hover:bg-rose-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          {showNewComplaint ? 'Cancel' : 'New Complaint'}
        </Button>
      </div>

      {/* Available Allowances Summary */}
      <Card className="border-rose-200">
        <CardHeader className="bg-gradient-to-r from-rose-50 to-transparent">
          <CardTitle className="flex items-center gap-2 text-rose-900">
            <Shield className="w-5 h-5 text-rose-600" />
            Available Complaint Allowances
          </CardTitle>
          <CardDescription>
            Pre-approved resolutions you can apply without manager approval
          </CardDescription>
        </CardHeader>
        <CardContent>
          {allowances.length === 0 ? (
            <Alert>
              <AlertTriangle className="w-4 h-4" />
              <AlertDescription>
                No complaint allowances configured. Ask your manager to set up allowances in Settings.
              </AlertDescription>
            </Alert>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {allowances.map((allowance, index) => (
                <div
                  key={index}
                  className="p-4 rounded-lg border bg-card space-y-2"
                >
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold">{allowance.complaint_type}</h4>
                    {allowance.frontdesk_can_approve && (
                      <Badge variant="outline" className="text-xs bg-green-50 text-green-700">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Approved
                      </Badge>
                    )}
                  </div>
                  <div className="space-y-1 text-sm">
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-muted-foreground" />
                      <span>Max: ${allowance.max_amount}</span>
                    </div>
                    <div>
                      <Badge className={getActionColor(allowance.action_type)}>
                        {allowance.action_type}
                      </Badge>
                    </div>
                    {allowance.notify_manager && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Bell className="w-3 h-3" />
                        <span className="text-xs">Notifies manager</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* New Complaint Form */}
      {showNewComplaint && (
        <Card className="border-rose-200">
          <CardHeader className="bg-gradient-to-r from-rose-50 to-transparent">
            <CardTitle className="text-rose-900">Log & Resolve Complaint</CardTitle>
            <CardDescription>
              Fill in the details and apply appropriate resolution
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 pt-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="guest_name">
                  Guest Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="guest_name"
                  value={newComplaint.guest_name}
                  onChange={(e) => setNewComplaint({ ...newComplaint, guest_name: e.target.value })}
                  placeholder="John Doe"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="room_number">Room Number</Label>
                <Input
                  id="room_number"
                  value={newComplaint.room_number}
                  onChange={(e) => setNewComplaint({ ...newComplaint, room_number: e.target.value })}
                  placeholder="305"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="complaint_type">
                Complaint Type <span className="text-destructive">*</span>
              </Label>
              <Select
                value={newComplaint.complaint_type}
                onValueChange={handleComplaintTypeChange}
              >
                <SelectTrigger id="complaint_type">
                  <SelectValue placeholder="Select complaint type" />
                </SelectTrigger>
                <SelectContent>
                  {allowances.map((allowance, index) => (
                    <SelectItem key={index} value={allowance.complaint_type}>
                      {allowance.complaint_type} (Max: ${allowance.max_amount})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={newComplaint.description}
                onChange={(e) => setNewComplaint({ ...newComplaint, description: e.target.value })}
                placeholder="Describe the issue..."
                rows={3}
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="resolution_action">Resolution Action</Label>
                <Select
                  value={newComplaint.resolution_action}
                  onValueChange={(value) => setNewComplaint({ ...newComplaint, resolution_action: value })}
                >
                  <SelectTrigger id="resolution_action">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="discount">Discount</SelectItem>
                    <SelectItem value="refund">Refund</SelectItem>
                    <SelectItem value="upgrade">Room Upgrade</SelectItem>
                    <SelectItem value="complimentary">Complimentary Service</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="amount_applied">
                  Amount Applied ($) <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="amount_applied"
                  type="number"
                  min="0"
                  step="1"
                  value={newComplaint.amount_applied}
                  onChange={(e) => setNewComplaint({ ...newComplaint, amount_applied: parseFloat(e.target.value) })}
                  placeholder="50"
                />
                {newComplaint.complaint_type && (
                  <p className="text-xs text-muted-foreground">
                    Max allowed: ${allowances.find(a => a.complaint_type === newComplaint.complaint_type)?.max_amount || 0}
                  </p>
                )}
              </div>
            </div>

            <Separator />

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowNewComplaint(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmitComplaint}
                className="bg-rose-600 hover:bg-rose-700"
                data-testid="submit-complaint-button"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Resolve & Apply
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Complaint History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Recent Complaints
          </CardTitle>
          <CardDescription>
            History of resolved guest complaints
          </CardDescription>
        </CardHeader>
        <CardContent>
          {complaints.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No complaints logged yet
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Guest</TableHead>
                  <TableHead>Room</TableHead>
                  <TableHead>Complaint Type</TableHead>
                  <TableHead>Resolution</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Resolved By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {complaints.map((complaint, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-muted-foreground" />
                        {new Date(complaint.resolved_at).toLocaleDateString()}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-muted-foreground" />
                        {complaint.guest_name}
                      </div>
                    </TableCell>
                    <TableCell>{complaint.room_number || '-'}</TableCell>
                    <TableCell>{complaint.complaint_type}</TableCell>
                    <TableCell>
                      <Badge className={getActionColor(complaint.resolution_action)}>
                        {complaint.resolution_action}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-semibold">${complaint.amount_applied}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {complaint.resolved_by_name}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
